import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';

import { useCallbackRef } from '../../../../core/hooks/useCallbackRef.js';
import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import type {
  DetailsRegionContextValue,
  LayoutMode,
} from '../../details-region/DetailsRegionContext.js';
import type { ExtractedDetailsSlot } from '../../details-region/slots.js';
import type { ExtractedSidebarSlot } from '../../sidebar-region/slots.js';
import type { RenderedMode, SidebarRenderedMode } from './panel-mode.js';
import { useBreakpointCollapse } from './useBreakpointCollapse.js';

/** @internal */
export interface UsePanelStateArgs<TBaseMode extends RenderedMode> {
  /** Controlled / uncontrolled props for the collapsed state. */
  collapsed: {
    value: boolean | undefined;
    defaultValue: boolean;
    onChange: ((value: boolean) => void) | undefined;
  };
  /** Container width below which the panel renders as a drawer. */
  breakpoint: number;
  /** Container element observed for size changes. */
  containerRef: RefObject<HTMLDivElement | null>;
  onBreakpointTransition: ((belowBreakpoint: boolean) => void) | undefined;
  /** See `useBreakpointCollapse`. `true` for the sidebar only. */
  autoCollapse: boolean;
  /**
   * Whether the consumer drives the panel width. A controlled width keeps
   * `intendedWidth` unset so the consumer's value stays authoritative.
   */
  isControlled: boolean;
  /** Mode rendered while the panel is open and above the breakpoint. */
  baseMode: TBaseMode;
  onResize: ((value: number) => void) | undefined;
}

/**
 * Panel state of one collapsible, resizable panel, parameterized over the mode
 * it renders in while open: the sidebar only ever renders `'split'`, while the
 * details panel follows its preferred layout. Keeping the base mode a type
 * parameter is what lets each panel publish exactly the modes it can reach.
 *
 * @internal
 */
export interface PanelState<TBaseMode extends RenderedMode> {
  /** Most recent measured container width (`0` until first measurement). */
  containerWidth: number;
  collapsed: boolean;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
  mode: TBaseMode | 'collapsed' | 'drawer';
  /** Width the user last dragged to, in px. `undefined` until dragged. */
  intendedWidth: number | undefined;
  commitWidth: (value: number) => void;
}

/**
 * The single owner of panel state for `PageLayout`: the collapsed state, the
 * container measurement, the drag width and the resulting rendered mode. Both
 * the sidebar and the details panel are this hook plus a slot-specific
 * projection of its result.
 *
 * @internal
 */
export function usePanelState<TBaseMode extends RenderedMode>({
  collapsed: collapsedProps,
  breakpoint,
  containerRef,
  onBreakpointTransition,
  autoCollapse,
  isControlled,
  baseMode,
  onResize,
}: UsePanelStateArgs<TBaseMode>): PanelState<TBaseMode> {
  const { containerWidth, collapsed, setCollapsed, isBelowBreakpoint } =
    useBreakpointCollapse({
      collapsed: collapsedProps,
      breakpoint,
      containerRef,
      onBreakpointTransition,
      autoCollapse,
    });

  const [intendedWidth, setIntendedWidth] = useState<number>();

  // Consumers pass an inline arrow, so the raw prop must not reach the
  // `useCallback` deps or every render would produce a new state object.
  const emitResize = useCallbackRef(onResize);
  const commitWidth = useCallback(
    (value: number) => {
      if (!isControlled) {
        setIntendedWidth(value);
      }
      emitResize(value);
    },
    [emitResize, isControlled],
  );

  let mode: TBaseMode | 'collapsed' | 'drawer' = baseMode;
  if (collapsed) {
    mode = 'collapsed';
  }
  if (isBelowBreakpoint) {
    mode = 'drawer';
  }

  return useMemo(
    () => ({
      collapsed,
      commitWidth,
      containerWidth,
      intendedWidth,
      mode,
      setCollapsed,
    }),
    [collapsed, commitWidth, containerWidth, intendedWidth, mode, setCollapsed],
  );
}

/**
 * Panel state of the sidebar. Owned by the `PageLayout.Sidebar` slot component
 * and published to the layout through the slot registry, so that unmounting or
 * key-replacing the slot discards it.
 *
 * @internal
 */
export type SidebarPanelState = Omit<
  PanelState<SidebarRenderedMode>,
  'containerWidth'
>;

/** @internal */
export function useSidebarPanelState({
  sidebar,
  containerRef,
}: {
  sidebar: Pick<ExtractedSidebarSlot, 'config'>;
  containerRef: RefObject<HTMLDivElement | null>;
}): SidebarPanelState {
  const { collapsed, setCollapsed, mode, intendedWidth, commitWidth } =
    usePanelState({
      collapsed: {
        value: sidebar.config.collapsed,
        defaultValue: sidebar.config.defaultCollapsed ?? false,
        onChange: sidebar.config.onCollapsedChange,
      },
      breakpoint: sidebar.config.breakpoint,
      containerRef,
      onBreakpointTransition: sidebar.config.onBreakpointTransition,
      autoCollapse: true,
      // `PageLayout.Sidebar` has no controlled-width prop, so its drag state is
      // always authoritative.
      isControlled: false,
      baseMode: 'split',
      onResize: sidebar.config.onResize,
    });

  // `containerWidth` is deliberately not published: nothing in the sidebar
  // region reads it, and republishing it would churn every registry consumer
  // on every container resize.
  return useMemo(
    () => ({ collapsed, commitWidth, intendedWidth, mode, setCollapsed }),
    [collapsed, commitWidth, intendedWidth, mode, setCollapsed],
  );
}

/**
 * Panel state of the details panel. Owned by the `PageLayout.Details` slot
 * component and published to the layout through the slot registry, so that
 * unmounting or key-replacing the slot discards it.
 *
 * A superset of `DetailsRegionContextValue`, so it can be handed straight to
 * `DetailsRegionContext.Provider`.
 *
 * @internal
 */
export interface DetailsPanelState extends DetailsRegionContextValue {
  containerWidth: number;
  /** Width the user last dragged to, in px. `undefined` until dragged. */
  intendedWidth: number | undefined;
  commitWidth: (value: number) => void;
}

/** @internal */
export function useDetailsPanelState({
  details,
  containerRef,
}: {
  details: Pick<ExtractedDetailsSlot, 'config'>;
  containerRef: RefObject<HTMLDivElement | null>;
}): DetailsPanelState {
  const [preferredLayout, setPreferredLayout] =
    useControllableState<LayoutMode>({
      value: details.config.layout,
      defaultValue: details.config.defaultLayout,
      onChange: details.config.onLayoutChange,
    });

  const {
    containerWidth,
    collapsed,
    setCollapsed,
    mode,
    intendedWidth,
    commitWidth,
  } = usePanelState({
    collapsed: {
      value: details.config.collapsed,
      defaultValue: details.config.defaultCollapsed ?? false,
      onChange: details.config.onCollapsedChange,
    },
    breakpoint: details.config.breakpoint,
    containerRef,
    onBreakpointTransition: details.config.onBreakpointTransition,
    autoCollapse: false,
    isControlled: details.config.width !== undefined,
    baseMode: preferredLayout,
    onResize: details.config.onResize,
  });

  return useMemo(
    () => ({
      collapsed,
      commitWidth,
      containerWidth,
      intendedWidth,
      mode,
      preferredLayout,
      setCollapsed,
      setPreferredLayout,
    }),
    [
      collapsed,
      commitWidth,
      containerWidth,
      intendedWidth,
      mode,
      preferredLayout,
      setCollapsed,
      setPreferredLayout,
    ],
  );
}
